import { DOCUMENT } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { Store } from '@ngxs/store';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxIconButtonComponent } from '@ui/icon-button/icon-button.component';
import { VxIconName } from '@ui/icons';
import { VxMenuItemComponent } from '@ui/menu/menu-item.component';
import {
  VxOrgOption,
  VxOrgSwitcherComponent,
} from '@ui/org-switcher/org-switcher.component';
import { VxSidebarFooterComponent } from '@ui/sidebar/sidebar-footer.component';
import { VxSidebarHeaderComponent } from '@ui/sidebar/sidebar-header.component';
import { VxSidebarComponent } from '@ui/sidebar/sidebar.component';
import { VxSidebarNavGroupComponent } from '@ui/sidebar-nav/sidebar-nav-group.component';
import { VxSidebarNavItemComponent } from '@ui/sidebar-nav/sidebar-nav-item.component';
import { VxSidebarNavComponent } from '@ui/sidebar-nav/sidebar-nav.component';
import { fullName } from 'src/app/models/user';
import { MenuService } from 'src/app/services/menu/menu.service';
import { CheckRoleDirective } from 'src/app/services/permission/permission.directive';
import { IncidentActivityState } from 'src/app/store/incident-activity/incident-activity.state';
import { SubscriptionState } from 'src/app/store/subscription/subscription.state';
import { UpdateUISettingAction } from 'src/app/store/ui-settings/ui-settings.actions';
import { UISettingsState } from 'src/app/store/ui-settings/ui-settings.state';
import {
  LogoutAction,
  SelectOrganizationAction,
} from 'src/app/store/user/user.actions';
import { UserState } from 'src/app/store/user/user.state';
import { ToastSnoozeIndicatorComponent } from './toast-snooze-indicator/toast-snooze-indicator.component';

const SUPPORT_URL = 'https://incidite.com/contact';

interface SidebarNavEntry {
  link: string;
  icon?: VxIconName;
  label: string;
  badge?: number;
}

/**
 * The assembled product sidebar: brand, routed nav from MenuService,
 * subscription notices, the toast snooze notice and the org/user switcher,
 * composed from the
 * domain-free vx-sidebar kit pieces.
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  imports: [
    CheckRoleDirective,
    TranslocoModule,
    VxButtonComponent,
    VxIconButtonComponent,
    VxMenuItemComponent,
    VxOrgSwitcherComponent,
    VxSidebarComponent,
    VxSidebarFooterComponent,
    VxSidebarHeaderComponent,
    VxSidebarNavComponent,
    VxSidebarNavGroupComponent,
    VxSidebarNavItemComponent,
    ToastSnoozeIndicatorComponent,
  ],
  host: { class: 'block h-full' },
})
export class SidebarComponent {
  private store = inject(Store);
  private router = inject(Router);
  private document = inject(DOCUMENT);
  private menuService = inject(MenuService);

  protected readonly supportUrl = SUPPORT_URL;

  protected user = this.store.selectSignal(UserState.getUser);

  protected currentOrgId = this.store.selectSignal(
    UserState.getCurrentOrganizationID
  );

  protected subscription = this.store.selectSignal(
    SubscriptionState.subscription
  );

  private collapsedSetting = this.store.selectSignal(
    UISettingsState.setting('isSidemenuCollapsed')
  );

  private activeIncidentCount = this.store.selectSignal(
    IncidentActivityState.activeIncidentCount
  );

  protected collapsed = computed(() => !!this.collapsedSetting());

  protected userName = computed(() => fullName(this.user()));

  protected orgs = computed<VxOrgOption[]>(
    () =>
      this.user()?.memberships.map(membership => ({
        id: membership.organizationId,
        name: membership.organization.name,
      })) ?? []
  );

  // MenuService rebuilds its item list imperatively (roles + feature flags),
  // so these are getters re-read on change detection, not signals.
  protected get mainNavEntries(): SidebarNavEntry[] {
    return this.navEntries.filter(entry => entry.link !== '/settings');
  }

  protected get settingsNavEntries(): SidebarNavEntry[] {
    return this.navEntries.filter(entry => entry.link === '/settings');
  }

  private get navEntries(): SidebarNavEntry[] {
    return this.menuService.getMenuItems().map(item => {
      const link = `/${item.routerLink}`;
      return {
        link,
        icon: item.icon as VxIconName | undefined,
        label: item.label,
        badge: this.badgeFor(link),
      };
    });
  }

  /** Active incident count on the Incidents entry; omitted while unknown and
   *  at 0 so the pill (and the compact rail's dot) disappears entirely. */
  private badgeFor(link: string): number | undefined {
    if (link !== '/incidents') {
      return undefined;
    }
    return this.activeIncidentCount() || undefined;
  }

  toggleCollapsed() {
    this.store.dispatch(
      new UpdateUISettingAction('isSidemenuCollapsed', !this.collapsed())
    );
  }

  selectOrganization(org: VxOrgOption) {
    if (org.id === this.currentOrgId()) {
      return;
    }

    this.store.dispatch(new SelectOrganizationAction(org.id));
    this.document.defaultView?.location.replace('/dashboard');
  }

  goToAccountSettings() {
    this.router.navigate(['/settings/account/general']);
  }

  openSupport() {
    this.document.defaultView?.open(SUPPORT_URL, '_blank');
  }

  logout() {
    this.store.dispatch(new LogoutAction());
  }

  /** Route to the subscription overview (not the upgrade flow) so the user can
   *  resolve a pending payment from the page that owns that flow. */
  goToSubscriptionSettings() {
    this.router.navigate(['/settings/organization/subscription']);
  }
}
